import React, { useEffect, useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Card,
  CardContent,
  Chip,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Assessment as AssessmentIcon,
  EmojiEvents as TrophyIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { getStudentGrades, getStudentGradesSummary } from '../services/gradeService';
import { GradeSummary } from '../types';
import { 
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  ArcElement
} from 'chart.js';
import {
  Bar as BarChart,
  Pie as PieChart,
  Doughnut as DoughnutChart
} from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
  ArcElement
);

interface StudentGradeSummaryDialogProps {
  open: boolean;
  onClose: () => void;
  studentId: number;
  studentName: string;
  examId?: number;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`student-summary-tabpanel-${index}`}
      aria-labelledby={`student-summary-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const StudentGradeSummaryDialog: React.FC<StudentGradeSummaryDialogProps> = ({
  open,
  onClose,
  studentId,
  studentName,
  examId
}) => {
  const [grades, setGrades] = useState<any[]>([]);
  const [summary, setSummary] = useState<GradeSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tabValue, setTabValue] = useState(0);

  const fetchData = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    setError('');
    try {
      const [gradesData, summaryData] = await Promise.all([
        getStudentGrades(studentId, examId),
        getStudentGradesSummary(studentId, examId)
      ]);
      
      const gradesWithDetails = gradesData.map(g => ({
        ...g,
        percentage: g.max_grade > 0 ? (g.grade / g.max_grade) * 100 : 0,
        status: g.max_grade > 0 && ((g.grade / g.max_grade) * 100) >= 60 ? 'passed' : 'failed'
      }));

      setGrades(gradesWithDetails);
      setSummary(summaryData);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to load student grade data');
    } finally {
      setLoading(false);
    }
  }, [studentId, examId]);

  useEffect(() => {
    if (open && studentId) {
      fetchData();
    }
  }, [open, studentId, fetchData]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Chart data for grade distribution
  const gradeDistributionData = grades.length > 0 ? {
    labels: ['Excellent (90-100%)', 'Good (80-89%)', 'Satisfactory (70-79%)', 'Needs Improvement (60-69%)', 'Failing (<60%)'],
    datasets: [{
      data: [
        grades.filter(g => g.percentage >= 90).length,
        grades.filter(g => g.percentage >= 80 && g.percentage < 90).length,
        grades.filter(g => g.percentage >= 70 && g.percentage < 80).length,
        grades.filter(g => g.percentage >= 60 && g.percentage < 70).length,
        grades.filter(g => g.percentage < 60).length
      ],
      backgroundColor: [
        '#4caf50', // Green for excellent
        '#8bc34a', // Light green for good
        '#ff9800', // Orange for satisfactory
        '#ff5722', // Red-orange for needs improvement
        '#f44336'  // Red for failing
      ],
      borderWidth: 2,
      borderColor: '#fff'
    }]
  } : undefined;

  // Chart data for performance comparison
  const performanceData = grades.length > 0 ? {
    labels: grades.slice(0, 10).map(g => g.course_title || 'N/A'),
    datasets: [{
      label: 'Percentage Score',
      data: grades.slice(0, 10).map(g => g.percentage),
      backgroundColor: 'rgba(54, 162, 235, 0.6)',
      borderColor: 'rgba(54, 162, 235, 1)',
      borderWidth: 1
    }]
  } : undefined;

  // Chart data for passing rate
  const passingRateData = grades.length > 0 ? {
    labels: ['Passed', 'Failed'],
    datasets: [{
      data: [grades.filter(g => g.status === 'passed').length, grades.filter(g => g.status === 'failed').length],
      backgroundColor: ['#4caf50', '#f44336'],
      borderWidth: 2,
      borderColor: '#fff'
    }]
  } : undefined;

  const getGradeColor = (percentage: number): 'success' | 'info' | 'warning' | 'error' => {
    if (percentage >= 80) return 'success';
    if (percentage >= 65) return 'info';
    if (percentage >= 50) return 'warning';
    return 'error';
  };

  const getGradeLetter = (percentage: number) => {
    if (percentage >= 80) return 'Exceeds Expectation';
    if (percentage >= 65) return 'Meets Expectation';
    if (percentage >= 50) return 'Approaches Expectation';
    return 'Below Expectation';
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon />
          Student Grade Summary: {studentName}
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        ) : grades.length > 0 && summary ? (
          <Box>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" gutterBottom>Student Details</Typography>
                    <Typography><strong>ID:</strong> {studentId}</Typography>
                    <Typography><strong>Name:</strong> {studentName}</Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" gutterBottom>Overall Statistics</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                      <Box sx={{ flex: '1 1 120px', textAlign: 'center' }}>
                        <Typography variant="h4" color="primary">
                          {summary.average_percentage?.toFixed(1) ?? '0.0'}%
                        </Typography>
                        <Typography variant="body2" color="text.secondary">Average Score</Typography>
                      </Box>
                      <Box sx={{ flex: '1 1 120px', textAlign: 'center' }}>
                        <Typography variant="h4" color="success.main">
                          {summary.passing_rate?.toFixed(1) ?? '0.0'}%
                        </Typography>
                        <Typography variant="body2" color="text.secondary">Passing Rate</Typography>
                      </Box>
                      <Box sx={{ flex: '1 1 120px', textAlign: 'center' }}>
                        <Typography variant="h6">
                          {summary.highest_score?.toFixed(1) ?? '0.0'}%
                        </Typography>
                        <Typography variant="body2" color="text.secondary">Highest Score</Typography>
                      </Box>
                      <Box sx={{ flex: '1 1 120px', textAlign: 'center' }}>
                        <Typography variant="h6">
                          {summary.lowest_score?.toFixed(1) ?? '0.0'}%
                        </Typography>
                        <Typography variant="body2" color="text.secondary">Lowest Score</Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Tabs for different views */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={tabValue} onChange={handleTabChange} aria-label="student summary tabs">
                <Tab label="Overview" />
                <Tab label="Grades" />
                <Tab label="Performance" />
                <Tab label="Insights" />
              </Tabs>
            </Box>

            {/* Overview Tab */}
            <TabPanel value={tabValue} index={0}>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, mb: 3 }}>
                <Box sx={{ flex: 1 }}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>Passing Rate</Typography>
                      <Box sx={{ maxWidth: 300, mx: 'auto' }}>
                        {passingRateData && <DoughnutChart data={passingRateData} />}
                      </Box>
                      <Box sx={{ mt: 2, textAlign: 'center' }}>
                        <Typography variant="h4" color="success.main">
                          {summary?.total_passed ?? 0}
                        </Typography>
                        <Typography variant="body2">Subjects Passed</Typography>
                        <Typography variant="h4" color="error.main">
                          {summary?.total_failed ?? 0}
                        </Typography>
                        <Typography variant="body2">Subjects Failed</Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>Grade Distribution</Typography>
                      <Box sx={{ maxWidth: 300, mx: 'auto' }}>
                        {summary?.grade_distribution && <PieChart data={{
                          labels: Object.keys(summary.grade_distribution),
                          datasets: [{
                            data: Object.values(summary.grade_distribution),
                            backgroundColor: [
                              '#4caf50', // Exceeds
                              '#2196f3', // Meets
                              '#ff9800', // Approaches
                              '#f44336'  // Below
                            ],
                            borderWidth: 2,
                            borderColor: '#fff'
                          }]
                        }} />}
                      </Box>
                    </CardContent>
                  </Card>
                </Box>
              </Box>
              <Box sx={{ width: '100%' }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Statistical Summary</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                      <Box sx={{ flex: '1 1 200px', textAlign: 'center' }}>
                        <Typography variant="h6" color="primary">
                          {summary?.median_score?.toFixed(2) ?? 'N/A'}
                        </Typography>
                        <Typography variant="body2">Median Score</Typography>
                      </Box>
                      <Box sx={{ flex: '1 1 200px', textAlign: 'center' }}>
                        <Typography variant="h6" color="primary">
                          {summary?.standard_deviation?.toFixed(2) ?? 'N/A'}
                        </Typography>
                        <Typography variant="body2">Standard Deviation</Typography>
                      </Box>
                      <Box sx={{ flex: '1 1 200px', textAlign: 'center' }}>
                        <Typography variant="h6" color="primary">
                          {summary?.total_assignments ?? 0}
                        </Typography>
                        <Typography variant="body2">Total Grades</Typography>
                      </Box>
                      <Box sx={{ flex: '1 1 200px', textAlign: 'center' }}>
                        <Typography variant="h6" color="primary">
                          {summary?.average_percentage?.toFixed(1) ?? '0.0'}%
                        </Typography>
                        <Typography variant="body2">Average Score</Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            </TabPanel>

            {/* Grades Tab */}
            <TabPanel value={tabValue} index={1}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Grades</Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Subject</TableCell>
                        <TableCell>Score</TableCell>
                        <TableCell>Percentage</TableCell>
                        <TableCell>Grade</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {grades.map((grade) => (
                        <TableRow key={grade.id}>
                          <TableCell>{grade.course_title || 'N/A'}</TableCell>
                          <TableCell>{grade.grade} / {grade.max_grade}</TableCell>
                          <TableCell>
                            <Chip 
                              label={`${grade.percentage?.toFixed(1) ?? '0.0'}%`}
                              color={getGradeColor(grade.percentage)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={getGradeLetter(grade.percentage)}
                              color={getGradeColor(grade.percentage)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip 
                              icon={grade.status === 'passed' ? <CheckCircleIcon /> : <CancelIcon />}
                              label={grade.status}
                              color={grade.status === 'passed' ? 'success' : 'error'}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabPanel>

            {/* Performance Tab */}
            <TabPanel value={tabValue} index={2}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Performance</Typography>
                  <Box sx={{ height: 400, mb: 3 }}>
                    {performanceData && <BarChart 
                      data={performanceData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                          y: {
                            beginAtZero: true,
                            max: 100
                          }
                        }
                      }}
                    />}
                  </Box>
                </CardContent>
              </Card>
            </TabPanel>

            {/* Insights Tab */}
            <TabPanel value={tabValue} index={3}>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, mb: 3 }}>
                <Box sx={{ flex: 1 }}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TrophyIcon color="primary" />
                        Top Performers
                      </Typography>
                      <List>
                        {grades.sort((a, b) => b.percentage - a.percentage).slice(0, 5).map((grade) => (
                          <ListItem key={grade.id}>
                            <ListItemAvatar>
                              <Avatar sx={{ bgcolor: getGradeColor(grade.percentage) }}>
                                <TrendingUpIcon />
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText 
                              primary={grade.course_title || 'N/A'}
                              secondary={`${grade.percentage?.toFixed(1) ?? '0.0'}% (${grade.grade} / ${grade.max_grade})`}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </CardContent>
                  </Card>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <WarningIcon color="error" />
                        Needs Attention
                      </Typography>
                      <List>
                        {grades.sort((a, b) => a.percentage - b.percentage).slice(0, 5).map((grade) => (
                          <ListItem key={grade.id}>
                            <ListItemAvatar>
                              <Avatar sx={{ bgcolor: 'error.main' }}>
                                <TrendingDownIcon />
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText 
                              primary={grade.course_title || 'N/A'}
                              secondary={`${grade.percentage?.toFixed(1) ?? '0.0'}% (${grade.grade} / ${grade.max_grade})`}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </CardContent>
                  </Card>
                </Box>
              </Box>
              <Box sx={{ width: '100%' }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Performance Insights</Typography>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
                      <Box sx={{ flex: 1, textAlign: 'center' }}>
                        <Typography variant="subtitle1" color="success.main">
                          <TrendingUpIcon /> Strong Performance
                        </Typography>
                        <Typography variant="body2">
                          {summary?.grade_distribution["Exceeds Expectation"] ?? 0} subjects with excellent scores (80%+)
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1, textAlign: 'center' }}>
                        <Typography variant="subtitle1" color="warning.main">
                          <AssessmentIcon /> Average Performance
                        </Typography>
                        <Typography variant="body2">
                          {summary?.grade_distribution["Meets Expectation"] ?? 0} subjects in good to satisfactory range
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1, textAlign: 'center' }}>
                        <Typography variant="subtitle1" color="error.main">
                          <TrendingDownIcon /> Needs Support
                        </Typography>
                        <Typography variant="body2">
                          {summary?.grade_distribution["Below Expectation"] ?? 0} subjects need additional support
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            </TabPanel>
          </Box>
        ) : (
          <Typography>No student grades data available.</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default StudentGradeSummaryDialog; 